import { get, groupBy, setWith, sortBy } from "lodash";
import type { GraphDataTreeNodeData } from "../GraphData";
import { getNodeResult } from "../../../../utils";

// Minimal inline types reflective of the portal usage
type Graph = { nodes: () => string[]; node: (id: string) => any };

const isRecord = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === "object";

const countChildValidationStates = (children: Record<string, any>) => {
  let warnings = 0;
  let errors = 0;

  Object.values(children).forEach((child) => {
    if (!isRecord(child)) return;
    if (child.validation === "warning" && child.value) warnings++;
    if (child.validation === "error" && child.value) errors++;
  });

  return { warnings, errors };
};

const propagateWarningsAndErrors = (node: any) => {
  let containsWarnings = (node.info?.warnings || 0) > 0;
  let containsErrors = (node.info?.errors || 0) > 0;
  let totalWarnings = node.info?.warnings || 0;
  let totalErrors = node.info?.errors || 0;
  if (node.children && node.children.length > 0) {
    node.children.forEach((child: any) => {
      const childResult = propagateWarningsAndErrors(child);
      containsWarnings = containsWarnings || childResult.containsWarnings;
      containsErrors = containsErrors || childResult.containsErrors;
      totalWarnings += childResult.totalWarnings;
      totalErrors += childResult.totalErrors;
    });
  }
  if (node.info) {
    node.info.containsWarnings = containsWarnings;
    node.info.containsErrors = containsErrors;
    node.info.totalWarnings = totalWarnings;
    node.info.totalErrors = totalErrors;
  }
  return { containsWarnings, containsErrors, totalWarnings, totalErrors };
};

export const mappingDataToTreeItem = (
  graph: Graph,
  debug: boolean,
  release: any,
): GraphDataTreeNodeData | undefined => {
  const mappingData: Record<string, any> = {};
  const entityPaths: Record<string, string> = { global: "" };
  if (!graph) return undefined;

  if (!debug) {
    if (release.relationships && release.relationships.length > 0) {
      const bySource = groupBy(release.relationships, "source");
      if (!bySource.global) return undefined;
      const buildEntities = (entities: any[], path = "") => {
        entities.forEach((rel) => {
          if (!rel.containment) return;
          entityPaths[rel.name] = `${path}${rel.name}.0.`;
          if (bySource[rel.target]) buildEntities(bySource[rel.target], `${entityPaths[rel.name]}`);
        });
      };
      buildEntities(bySource.global);
    }
  }

  let globalWarnings = 0;
  let globalErrors = 0;
  graph.nodes().forEach((n: string) => {
    const node: any = graph.node(n);
    if (!node) return;
    let path: any;
    if (node.entity === "global" && node.validation && node.derived) {
      if (node.validation.severity === "warning") globalWarnings++;
      if (node.validation.severity === "error") globalErrors++;
    }
    if (debug) {
      if (node.hidden) return;
      if (node.entity === "global") path = node.id;
      else if (typeof node.path === "string" && node.path.length > 0) path = node.path.split("/");
      else if (typeof node.id === "string" && node.id.length > 0) path = node.id.split("/");
      else return;
    } else {
      if (node.hidden) {
        path = `${entityPaths[node.entity]}${node.id}`;
      } else path = node.id;
    }
    const val = debug ? getNodeResult(node) : node.type;
    setWith(
      mappingData,
      path,
      {
        id: node.path || node.id,
        value: val,
        validation: get(node, "validation.severity"),
        description: node.validation ? `(${node.validation.severity}) ${node.validation.message}` : node.description,
      },
      Object,
    );
  });

  const sort = (data: Record<string, any>) => {
    return sortBy(Object.entries(data), (a) => {
      if ((a as any)[1].description) return (a as any)[1].description;
      else return `00${a[0]}`;
    });
  };

  const processChildren = (attrs: [string, any][], parent?: string): GraphDataTreeNodeData[] => {
    return attrs.flatMap<GraphDataTreeNodeData>(([id, attr]) => {
      if (!isRecord(attr)) return [];

      if (!attr.id) {
        let sub_children: any[] = [];
        if (debug) {
          sub_children = Object.entries(attr).flatMap(([instance, instanceValue]) => {
            if (!isRecord(instanceValue)) return [];
            const { warnings, errors } = countChildValidationStates(instanceValue);
            const content = instance;
            return [{
              id: `${parent ? `${parent}/` : ""}${id}/${instance}`,
              label: String(content),
              info: { type: "instance", text: String(content), value: "Instance", warnings, errors },
              children: instanceValue.id ? [] : processChildren(sort(instanceValue), `${parent ? `${parent}/` : ""}${id}/${instance}`),
            }];
          });
        } else {
          sub_children = processChildren(sort(isRecord(attr[0]) ? attr[0] : {}) as any);
        }
        return {
          id: `${parent ? `${parent}/` : ""}${id}`,
          label: id,
          info: { type: "entity", text: id, value: "Entity" },
          children: sub_children,
        };
      } else {
        return {
          id: attr.id,
          label: String(attr.description),
          info: { type: "attribute", text: String(attr.description), value: attr.value, nodeId: attr.id },
          children: [],
        };
      }
    });
  };

  const children = processChildren(sort(mappingData as any) as any);
  const result: GraphDataTreeNodeData = {
    children,
    id: "global",
    label: "Global",
    info: { type: "global", text: "Global", value: "Entity", warnings: globalWarnings, errors: globalErrors },
  };
  propagateWarningsAndErrors(result);
  return result;
};
